import { isOrderTransaction } from "features/orderModel";
import { useDispatch, useSelector } from "react-redux";
import { Store } from "store";
import { Settlement } from "./AccountSettlementHistory";
import { purchaseActions } from "modules/purchase";
import { useEffect, useMemo, useState } from "react";

type FetchSettlementHistory = {
  status: "LOADING" | "EMPTY" | "SUCCESS";
  filteredSettlementHistory: Settlement[];
  settlementYearList: {
    year: number;
    numOfSettlements: number;
  }[];
  filterSettlementHistory: (year: number | null) => void;
};

export const useFetchSettlementHistory = (): FetchSettlementHistory => {
  const dispatch = useDispatch();

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const activeTransactionMap = useSelector(
    (state: Store) => state.purchase.activeTransactionMap
  );

  useEffect(() => {
    dispatch(purchaseActions.fetchUserActiveTransactions.started());
  }, [dispatch]);

  const settlementHistory = activeTransactionMap
    ? (Object.values(activeTransactionMap)
        /**
         * @see https://github.com/balus-co-ltd/spwn/issues/1748#issuecomment-1163875595
         * @see https://github.com/balus-co-ltd/spwn/blob/b69d9da9ffa53de41729bd6a08138a86bc6e4a9a/packages/portal/src/utility/purchase.tsx#L127-L135
         * @description
         * statusが"PAYSUCCESS"の場合、決済履歴に表示する。
         * お支払い期限を過ぎても、オーダーのステータスが「CANCEL」にならないケースがある。
         * statusが"UNPROCESS", "AUTHPROCESS"の場合は、isCancelledがfalseかをチェックして決済履歴に表示する。
         */
        .filter((el) => {
          return (
            // @ts-expect-error TS2345
            ["PAYSUCCESS"].includes(el.status) ||
            // @ts-expect-error TS2345
            (["UNPROCESS", "AUTHPROCESS"].includes(el.status) &&
              el.isCancelled === false)
          );
        })
        /**
         * HOTFIX
         * productsフィールドがないものを除外。
         * 除外処理を入れているのは、移行処理でスタッフ分を移行していなかったため。対象orderが表示されないのは許容。
         * スタッフ分もproductsを移行したらこのfilter処理は不要。
         * @see https://github.com/balus-co-ltd/spwn/issues/323
         */
        .filter((el) => {
          return isOrderTransaction(el)
            ? el.lineItems && el.lineItems.length > 0
            : el.products && el.products.length > 0;
        })
        .sort(({ orderId: first }, { orderId: second }) => {
          return -(Number(first) - Number(second));
        }) as Settlement[])
    : [];

  const status = (() => {
    if (!activeTransactionMap) return "LOADING";
    if (Object.keys(activeTransactionMap).length === 0) return "EMPTY";
    return "SUCCESS";
  })();

  const settlementYearList = (() => {
    const countsMap = new Map<number, number>();

    for (const order of settlementHistory) {
      if (order.createdAt) {
        const date = order.createdAt.toDate();
        const year = date.getFullYear();
        countsMap.set(year, (countsMap.get(year) ?? 0) + 1);
      } else {
        console.warn("Settlement history has no valid date:", order);
        continue;
      }
    }

    const yearCountsArray = Array.from(countsMap.entries())
      .map(([year, numOfSettlements]) => ({ year, numOfSettlements }))
      .sort((a, b) => b.year - a.year);

    return yearCountsArray;
  })();

  const filterSettlementHistory = useMemo(
    () => (year: number | null) => {
      setSelectedYear(year);
    },
    []
  );

  return {
    status,
    filteredSettlementHistory: filterOrdersByYear(
      settlementHistory,
      selectedYear
    ),
    settlementYearList,
    filterSettlementHistory,
  };
};

const filterOrdersByYear = (
  orders: Settlement[],
  selectedYear: number | null
): Settlement[] => {
  if (selectedYear === null) {
    return orders;
  }

  return orders.filter((order) => {
    if (order.createdAt) {
      return order.createdAt.toDate().getFullYear() === selectedYear;
    }
    return false;
  });
};
